import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { UserErrors } from '@cutting/main/shared/other/response-errors';
import { fuseAnimations } from '@fuse/animations';
import { FuseConfigService } from '@fuse/services/config.service';
import {
  AvailableClientType,
  ChangeData,
  ClientConfirmModeEnum,
  ClientReginstering,
  CountryISO,
  CuttingClient,
  CuttingClientType,
  LocalizationHelper,
  MyValidator,
  PhoneNumberFormat,
  SearchCountryField,
} from 'cloud-shared-lib';
import { isNumber } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { environment } from '../../../../../../src/environments/environment';
import { RegisterService } from './register.service';

interface RegisterErrorResponse {
  code: UserErrors;
  message: string;
}

@Component({
  selector: 'fuse-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class FuseRegisterComponent implements OnInit, OnDestroy {
  registerForm: UntypedFormGroup;
  hidePass = true;
  sendingData = false;
  options: ClientReginstering;
  languages: any[];
  unsubscribe$: Subject<void> = new Subject();
  typeChange$: Observable<any>;
  adminCountry: CountryISO;
  countryCode$: Observable<string>;

  availableClientTypeEnum = AvailableClientType;

  searchCountryField = SearchCountryField;
  countryISO = CountryISO;
  phoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.Russia,
    CountryISO.Belarus,
    CountryISO.Ukraine,
    CountryISO.Poland,
    CountryISO.Latvia,
    CountryISO.Lithuania,
    CountryISO.Kazakhstan,
  ];

  get userId(): number {
    return LocalStorageManager.regData;
  }
  set userId(val: number) {
    LocalStorageManager.regData = +val;
  }

  constructor(
    private fuseConfigService: FuseConfigService,
    private formBuilder: UntypedFormBuilder,
    private registerService: RegisterService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private snackBar: MatSnackBar,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.languages = LocalizationHelper.getLanguageList;

    // this.selectedLanguage = this.languages.find(l => l.id === this.translate.currentLang);
    // if (!this.selectedLanguage) {
    //   this.selectedLanguage = this.languages[0];
    // }

    this.fuseConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        toolbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        sidepanel: {
          hidden: true,
        },
      },
    };
  }

  ngOnInit(): void {
    this.setRegData();

    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      surname: ['', [Validators.required]],
      patronymic: [''],
      type: [0, Validators.required],
      email: ['', [Validators.required, MyValidator.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      companyName: ['', Validators.maxLength(100)],
      phone: [''],
      isReadContract: [''],
      inn: [''],
    });

    this.typeChange$ = this.registerForm.controls.type.valueChanges.pipe(
      tap((val: CuttingClientType) => {
        if (this.options?.requiredINN && val === CuttingClientType.JuridicalPerson) {
          this.registerForm.controls.inn.setValidators([Validators.required]);
        } else {
          this.registerForm.controls.inn.clearValidators();
        }
      })
    );

    this.countryCode$ = this.registerService.getAdminCountryCode(this.userId).pipe(
      tap(res => {
        this.adminCountry = res as CountryISO;
      })
    );

    this.registerService.getRegisteredOptions(this.userId).then(response => {
      this.options = response;

      if (this.options.confirmMode !== ClientConfirmModeEnum.Email) {
        this.registerForm.controls.email.setValidators(MyValidator.email);
      }

      if (this.options.requiredPhoneNumber || this.options.confirmMode !== ClientConfirmModeEnum.Email) {
        this.registerForm.controls.phone.setValidators([Validators.required]);
      }

      if (this.options.availableClientType !== AvailableClientType.All) {
        this.registerForm.controls.type.setValue(
          this.options.availableClientType === AvailableClientType.Entity ? 1 : 0
        );
      }

      if (this.options.requiredINN && this.options.availableClientType === AvailableClientType.Entity) {
        this.registerForm.controls.inn.setValidators([Validators.required]);
      } else {
        this.registerForm.controls.inn.clearValidators();
      }

      if (this.options.requiredPatronymic) {
        this.registerForm.controls.patronymic.setValidators([Validators.required]);
      }

      if (this.options.clientAgreement.contractUrl !== '') {
        this.registerForm.controls.isReadContract.setValidators([Validators.requiredTrue]);
      }

      this.registerForm.updateValueAndValidity();
    });
  }

  setRegData(): void {
    this.activatedRoute.queryParams.pipe(filter(params => params.user)).subscribe(params => {
      if (isNumber(+params.user)) {
        this.userId = +params.user;
      }
    });
  }

  /**
   * Set the language
   * @param lang
   */
  changeLanguage(lang: string): void {
    if (this.isDevMode()) {
      location.reload();
    } else {
      LocalStorageManager.currentLang = lang;
      window.location.href = window.location.origin + '/cutting/' + lang + '/#' + this.getCurrentRoute();
    }
  }

  getCurrentRoute(): string {
    return this.router.url;
  }

  isDevMode(): boolean {
    return !environment.production;
  }

  /***********************************************************************************/

  onSubmit(): void {
    if (this.registerForm.valid) {
      this.sendingData = true;

      this.register(
        this.userId,
        new CuttingClient({
          email: this.registerForm.controls.email.value,
          surname: this.registerForm.controls.surname.value,
          name: this.registerForm.controls.name.value,
          patronymic: this.registerForm.controls.patronymic.value,
          type: this.registerForm.controls.type.value,
          password: this.registerForm.controls.password.value,
          companyName: this.registerForm.controls.companyName.value,
          languageCode: this.locale,
          phone: (this.registerForm.controls.phone.value as ChangeData)?.e164Number,
          INN: this.registerForm.controls.inn.value,
        })
      );
    }
  }

  register(userId: number, client: CuttingClient): void {
    this.registerService
      .register(userId, client)
      .then((idClient: number) => {
        client.id = idClient;

        this.sendingData = false;

        if (idClient > 0) {
          switch (this.options.confirmMode) {
            case ClientConfirmModeEnum.None:
              this.router.navigate(['/client/auth/account-activated']);
              break;

            case ClientConfirmModeEnum.Email:
              this.router.navigate(['/client/auth/mail-confirm'], { queryParams: { email: client.email } });
              break;

            case ClientConfirmModeEnum.Sms:
              this.router.navigate(['/client/auth/activate-account-by-sms-code'], {
                queryParams: { phone: client.phone, client: idClient },
              });
              break;
          }
        }
      })
      .catch((error: HttpErrorResponse) => {
        if (+error.status === 400) {
          const badReqStat: RegisterErrorResponse = JSON.parse(error.error);

          this.sendingData = false;

          switch (badReqStat.code) {
            case UserErrors.existed:
              this.snackBar.open($localize`Email или номер телефона уже зарегистрирован`);
              break;

            case UserErrors.invalidData:
              this.snackBar.open($localize`Данные введены некорректно`);
              break;

            case UserErrors.resendedMailForActivate:
              switch (this.options.confirmMode) {
                case ClientConfirmModeEnum.None:
                  this.router.navigate(['/client/auth/activate-account']);
                  break;

                case ClientConfirmModeEnum.Email:
                  this.router.navigate(['/client/auth/mail-confirm'], { queryParams: { email: client.email } });
                  break;

                case ClientConfirmModeEnum.Sms:
                  this.router.navigate(['/client/auth/activate-account-by-sms-code'], {
                    queryParams: { phone: client.phone, client: +badReqStat.message },
                  });
                  break;
              }
              // this.router.navigate(['/client/auth/mail-confirm'], { queryParams: { email: this.registerForm.controls.email.value } });
              break;

            default:
              break;
          }
        }
      });
  }

  hasRequiredValidator(control: string): boolean {
    const validator = this.registerForm.get(control).validator
      ? this.registerForm.get(control).validator({} as AbstractControl)
      : false;

    if (validator && validator.required) {
      return true;
    }

    return false;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
